import { db } from './database'

export const INITIAL_LAYOUT_RECOGNITION_KEY = 'initialLayoutRecognition'
const LOCK_NAME = 'maple-log:initial-layout-recognition'
type InitialLayoutState = 'pending' | 'completed'

/** Browser-wide onboarding history, independent of profile versions and capture sizes. */
export class InitialLayoutRecognition {
  state: InitialLayoutState | null = null

  constructor(private readonly onStorageError: () => void) {}

  async initialize(): Promise<void> {
    if (this.state !== null) return
    let initial: InitialLayoutState = 'completed'
    try {
      // Serialize first visits before either tab creates its default profile.
      await db.transaction('rw', db.settings, db.profiles, async () => {
        const saved = await db.settings.get(INITIAL_LAYOUT_RECOGNITION_KEY)
        if (saved?.value === 'pending' || saved?.value === 'completed') {
          initial = saved.value
          return
        }
        initial = await db.profiles.count() ? 'completed' : 'pending'
        await db.settings.put({ key: INITIAL_LAYOUT_RECOGNITION_KEY, value: initial, updatedAt: Date.now() })
      })
    } catch {
      this.onStorageError()
    }
    this.state = initial
  }

  async refresh(): Promise<void> {
    if (this.state === 'completed') return
    try {
      if ((await db.settings.get(INITIAL_LAYOUT_RECOGNITION_KEY))?.value === 'completed') this.state = 'completed'
    } catch { this.onStorageError() }
  }

  async complete(isCurrent: () => boolean): Promise<void> {
    if (!isCurrent() || this.state === 'completed') return
    try {
      await db.transaction('rw', db.settings, async () => {
        if (!isCurrent()) throw new Error('First-use capture changed')
        await db.settings.put({ key: INITIAL_LAYOUT_RECOGNITION_KEY, value: 'completed', updatedAt: Date.now() })
        // Throwing here also rolls back a write made while the source was replaced.
        if (!isCurrent()) throw new Error('First-use capture changed')
      })
    } catch {
      if (isCurrent()) this.onStorageError()
    }
    if (isCurrent()) this.state = 'completed'
  }
}

export async function withInitialLayoutLock(action: () => Promise<void>, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return
  if (!navigator.locks) return action()
  let started = false
  try {
    await navigator.locks.request(LOCK_NAME, { signal }, async () => {
      started = true
      if (!signal.aborted) await action()
    })
  } catch (error) {
    if (signal.aborted) return
    if (started) throw error
    // Some browsers expose the API but disallow locks by policy.
    await action()
  }
}
